import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Check, Phone, Timer, Wallet, ShieldCheck, BarChart3, Headphones } from "lucide-react";

const Tier = ({ seconds, price, features, highlight, testid }) => (
  <div
    data-testid={testid}
    className={`relative border bg-white p-8 ${highlight ? "border-black ring-1 ring-black" : "border-zinc-200"}`}
  >
    {highlight && (
      <span className="absolute -top-3 left-8 bg-[#0055FF] text-white text-[10px] font-bold tracking-[0.25em] uppercase px-2 py-1">
        Best Margin
      </span>
    )}
    <div className="font-heading font-black text-zinc-950 leading-none">
      <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
        {seconds}-Second Buffer
      </div>
      <div className="font-mono text-7xl tracking-tighter">${price}</div>
      <div className="mt-1 text-xs font-mono text-zinc-500 tracking-wider uppercase">
        per qualified lead
      </div>
    </div>
    <ul className="mt-8 space-y-3 text-sm">
      {features.map((f) => (
        <li key={f} className="flex gap-2 items-start">
          <Check className="w-4 h-4 mt-0.5 text-[#0055FF] flex-shrink-0" />
          <span className="text-zinc-700">{f}</span>
        </li>
      ))}
    </ul>
    <Link to="/register" className="block mt-8">
      <Button className="w-full btn-sharp bg-black hover:bg-zinc-800 text-white text-sm tracking-wider uppercase font-bold py-6" data-testid={`${testid}-cta`}>
        Choose {seconds}s tier <ArrowRight className="ml-2 w-4 h-4" />
      </Button>
    </Link>
  </div>
);

export default function Landing() {
  return (
    <div>
      {/* HERO */}
      <section className="relative border-b border-zinc-200 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="tag" data-testid="landing-tag">
              <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full ring-pulse" /> Live FEX inbound routing
            </span>
            <h1 className="font-heading font-black text-zinc-950 tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl mt-6">
              Inbound FEX leads.
              <br />
              <span className="text-[#0055FF]">Phone rings.</span> You close.
            </h1>
            <p className="mt-6 text-lg text-zinc-700 max-w-xl">
              Real callers, real intent. Final-expense seniors see our ad, dial in, and our system routes them straight to your cell — with a billing buffer that protects you from misfires.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button className="btn-sharp bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm tracking-wider uppercase font-bold py-6 px-8" data-testid="landing-hero-cta">
                  Start receiving calls <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="#pricing">
                <Button variant="outline" className="btn-sharp text-sm tracking-wider uppercase font-bold py-6 px-8" data-testid="landing-pricing-link">
                  See pricing
                </Button>
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[
                ["75s / 115s", "buffer protection"],
                ["$40–$60", "per lead"],
                ["Live", "call transfer"],
              ].map(([a, b]) => (
                <div key={a}>
                  <div className="font-mono text-2xl font-bold tracking-tight">{a}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{b}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              {/* Console preview card */}
              <div className="bg-zinc-950 text-white border border-zinc-950 aspect-[4/5] p-7 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                    <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                    <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                  </div>
                  <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Agent Console</div>
                </div>

                <div className="space-y-6">
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Status</div>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="w-3 h-3 rounded-full bg-[#10B981] ring-pulse" />
                      <span className="font-heading font-black text-3xl tracking-tight">READY</span>
                    </div>
                  </div>

                  <div className="border-t border-zinc-800 pt-4">
                    <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Incoming caller</div>
                    <div className="font-mono font-bold text-2xl mt-1.5">+1 (407) ••• ••42</div>
                    <div className="text-xs text-zinc-400 font-mono mt-1">FL · Final Expense intent</div>
                  </div>

                  <div className="border-t border-zinc-800 pt-4 grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Buffer</div>
                      <div className="font-mono font-bold text-2xl mt-1">115s</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Lead price</div>
                      <div className="font-mono font-bold text-2xl mt-1">$60</div>
                    </div>
                  </div>

                  <div className="border-t border-zinc-800 pt-4">
                    <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Wallet</div>
                    <div className="font-mono font-bold text-2xl mt-1 text-[#0055FF]">$1,240.00</div>
                  </div>
                </div>

                <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">
                  ● Live — connecting next call
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white border border-zinc-200 p-4 w-64 shadow-[8px_8px_0_0_rgba(0,0,0,1)] hidden sm:block">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Just billed</div>
                <div className="font-mono font-bold text-lg mt-1">+$60.00 lead</div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-xs text-zinc-700">Buffer cleared 115s</div>
                  <span className="w-2.5 h-2.5 bg-[#10B981] rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <h2 className="font-heading font-black text-4xl lg:text-5xl tracking-tight leading-none">
              How the call lands
            </h2>
            <div className="text-xs font-mono tracking-widest text-zinc-500 uppercase">01 → 04</div>
          </div>
          <div className="grid md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
            {[
              { i: Phone, t: "Senior dials FEX ad", d: "Our marketing brings warm callers in across 50 states." },
              { i: Timer, t: "Routed to your phone", d: "Our system transfers the live call to your cell instantly." },
              { i: ShieldCheck, t: "Buffer protects you", d: "75 or 115-second buffer. Hang-ups don't bill." },
              { i: Wallet, t: "Auto-deducted", d: "Lead fee comes out of your prepaid wallet — done." },
            ].map((s, idx) => (
              <div key={s.t} className="bg-white p-8">
                <div className="text-[10px] font-mono tracking-[0.3em] text-zinc-400">0{idx + 1}</div>
                <s.i className="w-7 h-7 mt-4 text-[#0055FF]" strokeWidth={1.5} />
                <div className="font-heading font-bold text-lg mt-4 tracking-tight">{s.t}</div>
                <div className="text-sm text-zinc-600 mt-2 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="max-w-2xl mb-12">
            <span className="tag">Two tiers. Zero subscriptions.</span>
            <h2 className="font-heading font-black text-4xl lg:text-5xl tracking-tight leading-none mt-4">
              Pick your buffer. <br />Pay only for keepers.
            </h2>
            <p className="text-zinc-700 mt-4 text-base">
              Calls that hang up before the buffer ends are <span className="font-semibold">free</span>. You only pay when a caller crosses your buffer — meaning you got a real, working FEX prospect on the line.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            <Tier
              seconds={75}
              price={40}
              testid="pricing-tier-75"
              features={[
                "75-second protection window",
                "$40 per qualified lead",
                "Live call transfer to your cell",
                "Real-time wallet dashboard",
                "Call recordings included",
              ]}
            />
            <Tier
              seconds={115}
              price={60}
              testid="pricing-tier-115"
              highlight
              features={[
                "115-second protection window",
                "$60 per qualified lead",
                "Priority in call routing queue",
                "Live call transfer to your cell",
                "Real-time wallet dashboard",
                "Call recordings included",
              ]}
            />
          </div>
          <div className="mt-10 text-xs font-mono tracking-widest uppercase text-zinc-500">
            PCI-secure card checkout. Wallet top-ups from $100.
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-zinc-950 text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-heading font-black text-4xl lg:text-5xl tracking-tight leading-none">
              Built for the agent
              <br />
              who hates dead leads.
            </h2>
            <p className="text-zinc-300 mt-6 max-w-md text-base">
              Every wasted dial costs you commission. Inbound FEX flips the script — the senior calls you, ready to talk. We just hand you the phone.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-px bg-zinc-800">
            {[
              { i: Headphones, t: "Real intent", d: "Callers responded to a direct FEX ad. They want to talk." },
              { i: Timer, t: "Buffer defense", d: "Misdial, wrong number, hang-up? Doesn't bill." },
              { i: BarChart3, t: "Live ledger", d: "Track every lead, dollar, and call duration in real time." },
              { i: ShieldCheck, t: "Bank-grade security", d: "Wallet top-ups on encrypted, PCI-compliant checkout." },
            ].map((b) => (
              <div key={b.t} className="bg-zinc-950 p-8">
                <b.i className="w-6 h-6 text-[#0055FF]" strokeWidth={1.5} />
                <div className="font-heading font-bold text-lg mt-4 tracking-tight">{b.t}</div>
                <div className="text-sm text-zinc-400 mt-2">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-6 py-20 lg:py-28 text-center">
          <h2 className="font-heading font-black text-5xl lg:text-7xl tracking-tighter leading-none">
            Your next FEX lead is dialing right now.
          </h2>
          <p className="mt-6 text-zinc-700 text-lg max-w-xl mx-auto">
            Set up takes two minutes. Approve, top up, go ready, and your phone starts ringing.
          </p>
          <Link to="/register">
            <Button className="mt-10 btn-sharp bg-black hover:bg-zinc-800 text-white text-sm tracking-wider uppercase font-bold py-7 px-10" data-testid="landing-cta-bottom">
              Claim your seat <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="py-8 text-center text-xs font-mono tracking-widest uppercase text-zinc-500">
        © LeadBridge — Inbound Lead Routing for Life Insurance Agents
      </footer>
    </div>
  );
}
